import { Card, Muted, Stack, color, space, status } from '@biopeak/ui';
import { useHemogramFindings, type HemogramFinding } from '../lib/documents.ts';

/**
 * Padrões multivariados do hemograma, recalculados a cada revisão de exame
 * confirmada (`private.evaluate_hemogram_patterns`, chamada de dentro de
 * `review_document`).
 *
 * Só existe quando há hemograma confirmado — some sozinho quando não há
 * achado, igual ao resto do painel: uma seção vazia gritando "sem alerta"
 * ocuparia espaço para dizer nada.
 *
 * A explicação de cada achado já termina com o aviso de que é sugestão, não
 * diagnóstico (gravado assim no banco); o texto abaixo do título reforça o
 * mesmo ponto uma vez, no nível da seção, para quem não lê cada linha.
 */
export function HemogramFindingsCard({ patientId }: { patientId: string }) {
  const achados = useHemogramFindings(patientId);

  if (!achados.data || achados.data.length === 0) return null;

  return (
    <div className="doctor-overview-card--wide">
      <Card>
        <Stack gap={space.md}>
          <span style={{ fontSize: '17px', fontWeight: 600 }}>
            Padrões do hemograma
          </span>
          <Muted style={{ fontSize: '13px' }}>
            Cruzamento automático de analitos do hemograma confirmado — sugestão
            de hipótese, nunca diagnóstico.
          </Muted>
          <Stack gap={space.sm}>
            {achados.data.map((achado) => (
              <Achado key={achado.id} achado={achado} />
            ))}
          </Stack>
        </Stack>
      </Card>
    </div>
  );
}

const TOM: Record<string, { fg: string; bg: string; border: string }> = {
  alerta: status.red,
  atencao: status.yellow,
  info: status.green,
};

function Achado({ achado }: { achado: HemogramFinding }) {
  const tom = TOM[achado.severity] ?? status.green;
  const evidencias = Array.isArray(achado.evidence)
    ? (achado.evidence as Array<{
        display_name: string;
        value: number;
        unit: string;
      }>)
    : [];

  return (
    <div
      style={{
        borderLeft: `3px solid ${tom.border}`,
        background: tom.bg,
        borderRadius: '4px',
        padding: `${space.xs} ${space.sm}`,
      }}
    >
      <span style={{ fontSize: '14px', fontWeight: 600, color: tom.fg }}>
        {achado.title}
      </span>
      <p style={{ margin: '4px 0 0', fontSize: '13px', lineHeight: 1.5, color: color.text }}>
        {achado.explanation}
      </p>
      {evidencias.length > 0 && (
        <p style={{ margin: '4px 0 0', fontSize: '12px', color: color.textSecondary }}>
          {evidencias
            .map((e) => `${e.display_name}: ${e.value}${e.unit ? ` ${e.unit}` : ''}`)
            .join(' · ')}
        </p>
      )}
    </div>
  );
}
